import {
    Icon,
    MenuItemTooltip,
    type MenuItem,
    type MenuItemCommandEvent,
} from '@ics-portal/guardian';
import { type ReactNode } from 'react';
import {
    type CanFn,
    type Permission,
} from '@pages/project/_utils/access/project-permissions';
import { simplePluralize } from '@utils/stringFormatUtils';
import {
    type BaseItemState,
    type CellRendererParams,
    TableAction,
    type TableActionsConfig,
} from '../types';

const ACTION_PERMISSION: Record<TableAction, Permission> = {
    [TableAction.CREATE]: 'content:create',
    [TableAction.EDIT]: 'content:edit',
    [TableAction.DELETE]: 'content:delete',
};

const actionMenuItemMapping: Record<TableAction, MenuItem> = {
    [TableAction.CREATE]: {
        label: 'Create',
        icon: <Icon name="add_box" />,
    },
    [TableAction.EDIT]: {
        label: 'Edit',
        icon: <Icon name="edit" />,
    },
    [TableAction.DELETE]: {
        label: 'Delete',
        icon: <Icon name="delete" />,
    },
};

function normalizeItemState(result: boolean | BaseItemState): boolean {
    if (typeof result === 'boolean') {
        return result;
    }
    return result.visible ?? false;
}

function createTooltipTemplate(
    action: TableAction,
    tooltip: string,
): (item: MenuItem) => ReactNode {
    return function TooltipTemplate(item: MenuItem): ReactNode {
        return (
            <MenuItemTooltip
                id={`${action.toLowerCase()}-action`}
                tooltip={tooltip}
                position="left"
                item={item}
            />
        );
    };
}

export function prepareContextMenuItems<T>(
    actionsConfig: TableActionsConfig<T> | undefined,
    params: CellRendererParams<T>,
    can?: CanFn,
): MenuItem[] {
    if (!actionsConfig) {
        return [];
    }
    const {
        baseItems,
        customItems,
        entityName,
        handler,
        shouldShowBaseItem,
        shouldShowCustomItem,
    } = actionsConfig;

    const hasAnyRowsSelected = Boolean(params.context.selectedRows.length);

    const getActionState = (
        action: TableAction,
        cellRendererParams: CellRendererParams<T>,
    ): BaseItemState => {
        const showBaseItem: boolean =
            typeof shouldShowBaseItem === 'function'
                ? normalizeItemState(
                      shouldShowBaseItem(action, cellRendererParams),
                  )
                : true;

        if (action === TableAction.CREATE && showBaseItem && !hasAnyRowsSelected) {
            return { visible: true };
        }

        if (
            [TableAction.DELETE, TableAction.EDIT].includes(action) &&
            hasAnyRowsSelected &&
            showBaseItem
        ) {
            return { visible: true };
        }

        return { visible: false };
    };

    const defaultActionHandler = (
        baseItem: TableAction,
    ): Partial<MenuItem> => ({
        data: {
            type: baseItem,
        },
        command: (event: MenuItemCommandEvent) => {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access -- primereact doesn't have strong type for menuitem data
            handler ? handler(params, event.item.data?.type) : null;
        },
    });

    const baseItemsDefinition: MenuItem[] = (baseItems ?? [])
        .map((action: TableAction): MenuItem | null => {
            const state = getActionState(action, params);
            if (state.visible === false) return null;

            // Hide mutating actions entirely when the user lacks the permission.
            if (can && !can(ACTION_PERMISSION[action])) return null;

            const selectedCount = params.context.selectedRows.length;
            const resolvedEntityName =
                entityName && action !== TableAction.CREATE
                    ? simplePluralize(entityName, selectedCount)
                    : entityName;

            const menuItem: MenuItem = {
                ...actionMenuItemMapping[action],
                ...(resolvedEntityName && {
                    label: `${actionMenuItemMapping[action].label ?? ''} ${resolvedEntityName}`,
                }),
                ...defaultActionHandler(action),
                disabled: state.disabled,
            };

            if (state.disabled && state.tooltip) {
                menuItem.template = createTooltipTemplate(
                    action,
                    state.tooltip,
                );
            }

            return menuItem;
        })
        .filter((item): item is MenuItem => item !== null);

    const customItemsArray =
        typeof customItems === 'function'
            ? customItems(params)
            : customItems ?? [];

    const customItemsFiltered = customItemsArray
        .filter((customItem) => {
            // Hide mutating custom items when the user lacks the permission.
            if (customItem.permission && can && !can(customItem.permission)) {
                return false;
            }

            if (shouldShowCustomItem) {
                return shouldShowCustomItem(customItem, params);
            }

            if (!customItem.onSelected) {
                return true;
            }
            return hasAnyRowsSelected;
        })
        .map((customItem) => ({
            ...customItem,
            command: customItem.handler
                ? () => customItem.handler?.(params)
                : undefined,
        }));

    return [...baseItemsDefinition, ...customItemsFiltered];
}
